/**
 * Wellness rollup formatting — shared, and deliberately free of React.
 *
 * These three lived on VitalsCard, which was fine while a component was the
 * only consumer. The Apple Watch bridge needs them too: the phone formats the
 * rollup once and ships display-ready strings, so the watch never reimplements
 * per-key rules and the two surfaces can't drift into describing the same day
 * differently.
 *
 * But `useWatchSync` importing a component module would drag React Navigation,
 * AsyncStorage, the Ably context and the icon set into the bridge hook's module
 * graph — and into every jest suite that touches it. Plain functions belong in
 * a plain module. VitalsCard re-exports them so existing importers are
 * unaffected.
 */

export const WELLNESS_STATUS_STYLE: Record<string, { label: string; color: string }> = {
  active_now: { label: 'Active now', color: '#10B981' },
  active_recently: { label: 'Active recently', color: '#10B981' },
  quiet_expected: { label: 'Quiet', color: '#6B7280' },
  unusually_quiet: { label: 'Quieter than usual', color: '#F59E0B' },
  signals_offline: { label: 'Signals offline', color: '#6B7280' },
  paused: { label: 'Paused', color: '#6B7280' },
};

export function relTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  // Floor, never round: "1h ago" at 1h59m understates staleness politely,
  // but "2h ago" at 1h31m would overstate recency's inverse — floor is the honest bound.
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function vitalSummary(v: any): string {
  switch (v.key) {
    case 'moved_today':
      if (v.state === 'source_offline') return `no sync ${relTime(v.last_sync_at) || 'yet'}`;
      return v.steps_today != null ? `${v.steps_today.toLocaleString()} steps` : 'no steps yet';
    case 'meds_today':
      if (v.expected == null || v.expected === 0) return v.self_marked > 0 ? `${v.self_marked} marked` : 'none scheduled';
      return `${v.self_marked}/${v.expected} marked`;
    case 'app_activity':
      return v.last_seen_at ? relTime(v.last_seen_at) : 'not yet today';
    case 'out_and_about':
      if (v.last_transition) return `${v.last_transition.type === 'arrived' ? 'arrived' : 'out'} ${relTime(v.last_transition.at)}`;
      return v.state === 'source_offline' ? 'no location data' : 'home so far';
    default:
      return v.state;
  }
}
